import React from 'react'
import {
  Box,
  Text,
  Color,
  FontWeight,
  Display,
  FlexDirection,
  GridDisplay,
  JustifyContent,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

import { formatPrice } from './orderSummaryPricing'
import type { DeliveryPricing, DeliveryPricingItem } from './types'

type BasketRecipe = {
  recipe: Recipe
  quantity: number
}

type FlexRecipeRow = {
  recipe: Recipe
  description: string | null
  price: string
}

type FlexOrderSummaryProps = {
  basketRecipes: BasketRecipe[]
  deliveryPricing: DeliveryPricing
  numberOfRecipes: number
  recipeTotal: string
}

type GetFlexRecipeRowsArgs = {
  basketRecipes: BasketRecipe[]
  deliveryPricing: DeliveryPricing
  numberOfRecipes: number
}

const isNonZeroPrice = (price?: string | null) => {
  const value = Number(price)

  return !Number.isNaN(value) && value !== 0
}

const getFlexRecipeDescription = ({
  quantity,
  pricePerPortion,
}: {
  quantity?: string | null
  pricePerPortion?: string | null
}) => {
  const quantityValue = Number(quantity)
  const portions =
    Number.isInteger(quantityValue) && quantityValue > 0
      ? `${quantityValue} ${quantityValue === 1 ? 'portion' : 'portions'}`
      : null
  const perPortion = isNonZeroPrice(pricePerPortion)
    ? `${formatPrice(pricePerPortion)} per portion`
    : null

  return [portions, perPortion].filter(Boolean).join(' • ') || null
}

const findBasketRecipe = (basketRecipes: BasketRecipe[], item: DeliveryPricingItem) =>
  basketRecipes.find(({ recipe }) => recipe.id === item.id)?.recipe

const getFlexRecipeRows = ({
  basketRecipes,
  deliveryPricing,
  numberOfRecipes,
}: GetFlexRecipeRowsArgs) => {
  const recipeItems = deliveryPricing.items?.filter(
    (item) => item.type === 'Recipe' && isNonZeroPrice(item.listPrice),
  )

  if (!recipeItems) {
    return []
  }

  const rows = recipeItems.reduce<FlexRecipeRow[]>((flexRows, item) => {
    const recipe = findBasketRecipe(basketRecipes, item)

    if (!recipe) {
      return flexRows
    }

    flexRows.push({
      recipe,
      description: getFlexRecipeDescription(item),
      price: formatPrice(item.listPrice),
    })

    return flexRows
  }, [])

  if (rows.length !== numberOfRecipes) {
    return []
  }

  if (!rows.every(({ recipe }) => recipe.shelfLife?.maxDays != null)) {
    return rows
  }

  return [...rows].sort(
    (a, b) =>
      (a.recipe.shelfLife?.maxDays ?? Number.MAX_SAFE_INTEGER) -
      (b.recipe.shelfLife?.maxDays ?? Number.MAX_SAFE_INTEGER),
  )
}

export const FlexOrderSummary = ({
  basketRecipes,
  deliveryPricing,
  numberOfRecipes,
  recipeTotal,
}: FlexOrderSummaryProps) => {
  const flexRecipeRows = getFlexRecipeRows({ basketRecipes, deliveryPricing, numberOfRecipes })

  if (flexRecipeRows.length === 0) {
    return (
      <Box
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        data-no-code="orderSummaryRecipesSubtotal"
      >
        <Text fontWeight={FontWeight.Bold}>Recipes ({numberOfRecipes})</Text>
        <Text fontWeight={FontWeight.Bold}>{recipeTotal}</Text>
      </Box>
    )
  }

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      data-no-code="orderSummaryRecipesSubtotal"
    >
      <Text size={2} color={Color.ColdGrey_600}>
        Recipes with more portions have a better price per portion.
      </Text>
      {flexRecipeRows.map(({ recipe, description, price }, index) => (
        <Box
          key={`${recipe.id}-${index}`}
          display={GridDisplay.Grid}
          gridTemplateColumns="minmax(0, 68%) minmax(0, 1fr)"
          gap={4}
          style={{ alignItems: 'start' }}
        >
          <Box minWidth="0">
            <Text
              data-no-code={`orderSummaryRecipeName-${index}`}
              fontWeight={FontWeight.Bold}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {recipe.name}
            </Text>
            {description && (
              <Text
                size={2}
                color={Color.ColdGrey_600}
                data-no-code={`orderSummaryRecipeServings-${index}`}
              >
                {description}
              </Text>
            )}
          </Box>
          <Text
            fontWeight={FontWeight.Bold}
            style={{ justifySelf: 'end' }}
            data-no-code={`orderSummaryRecipePrice-${index}`}
          >
            {price}
          </Text>
        </Box>
      ))}
    </Box>
  )
}
